import { Injectable, inject } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { hasRole } from '@shared/auth/has-role';
import type { User } from '@shared/models/user';
import { AuthUser, PinnedBranch } from '@tvd/data-access/auth';
import { environment } from '@env';
import { filter } from 'rxjs';
import { resolveBillingRoute } from './billing-route';
import { SubscriptionStore } from './subscription-store';

/**
 * Reactive paywall redirector.
 *
 * `subscriptionGuard` on the /dashboard route's `canActivateChild` does not
 * reliably fire on every deep child activation under the lazy-loaded
 * dashboard module — so a paywalled owner could navigate between
 * customers / services / settings pages without ever being redirected.
 *
 * This service complements the guard by listening to every `NavigationEnd`
 * and pushing paywalled owners to `/dashboard/{org}/{branch}/settings/billing`
 * whenever the subscription store reports `needsPayment`. It does NOT bypass
 * the dormancy gate (no-op when `enablePurchases=false`) and short-circuits
 * on the billing page itself to avoid a loop.
 *
 * Instantiate once at app shell entry by adding `inject(PaywallRedirector)`
 * to the root component constructor (DI side-effect — no return value used).
 */
@Injectable({ providedIn: 'root' })
export class PaywallRedirector {
  private readonly router = inject(Router);
  private readonly authUser = inject(AuthUser);
  private readonly pinnedBranch = inject(PinnedBranch);
  private readonly store = inject(SubscriptionStore);

  /**
   * Re-entry guard. Three signals + NavigationEnd all call tick() in rapid
   * succession on login (URL change, user data load, paymentRequirement
   * API response). Without this, `router.url` may still report the old URL
   * during the first in-flight navigation, so subsequent ticks issue
   * another router.navigate() to the SAME destination — the two
   * navigations interfere and the user ends up bounced back to the
   * original URL.
   */
  private isRedirecting = false;

  constructor() {
    if (!environment.enablePurchases) {
      return;
    }

    // React to THREE independent triggers so the redirect fires whichever
    // signal updates last:
    //   1. NavigationEnd — catches URL changes (e.g. login -> /dashboard).
    //   2. authUser.data — catches the moment user data loads after login
    //      (NavigationEnd may have fired before user was populated).
    //   3. paymentRequirement signal — catches the moment the /needs-payment
    //      response arrives (the initial fetch is async; we need to react
    //      to its emission, not just to the trigger that fired the fetch).
    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        takeUntilDestroyed(),
      )
      .subscribe(() => this.tick());

    toObservable(this.authUser.data)
      .pipe(takeUntilDestroyed())
      .subscribe(() => this.tick());

    toObservable(this.store.paymentRequirement)
      .pipe(takeUntilDestroyed())
      .subscribe(() => this.tick());
  }

  private tick(): void {
    const user = this.authUser.data();
    if (!user || !hasRole(user, 'owner')) return;
    if (this.router.url.includes('/settings/billing')) return;

    const cached = this.store.paymentRequirement();
    if (cached) {
      this.maybeRedirect(cached.needsPayment, user);
      return;
    }

    // No cached state yet — fire the fetch. The paymentRequirement
    // subscription above will re-tick when the response lands and trigger
    // the redirect path.
    this.store.checkPaymentRequirement().subscribe({
      error: () => {
        // swallow — failing open is preferable to locking the user
        // out of the whole app on a transient API blip.
      },
    });
  }

  private maybeRedirect(needsPayment: boolean, user: User): void {
    if (!needsPayment) return;
    if (this.isRedirecting) return;
    if (this.router.url.includes('/settings/billing')) return;
    const inflight = this.router.getCurrentNavigation();
    if (
      inflight &&
      inflight.finalUrl?.toString().includes('/settings/billing')
    ) {
      return;
    }

    const route = resolveBillingRoute(
      this.router.url,
      user,
      this.pinnedBranch.org(),
      this.pinnedBranch.branch(),
    );
    // resolveBillingRoute returns ['/dashboard'] when it can't resolve a
    // complete URL — don't navigate to that (would just retrigger us).
    if (route.length < 5) return;

    this.isRedirecting = true;
    this.router.navigate([...route]).finally(() => {
      this.isRedirecting = false;
    });
  }
}
